export type RefreshFailureKind =
  | 'invalid-session'
  | 'csrf-rejected'
  | 'concurrent-refresh'
  | 'rate-limited'
  | 'server-error'
  | 'timeout'
  | 'offline'
  | 'network-error';

export interface RefreshFailure {
  kind: RefreshFailureKind;
  definitive: boolean;
}

/**
 * Die Heimatsitzung ließ sich gerade nicht erneuern; der Request, der sie brauchte, ging nicht
 * raus. Ein eigener Fehler, damit niemand die Antwort von /auth/refresh — etwa 409 oder ein
 * CSRF-403 — für die Antwort seines eigenen Requests hält.
 */
export class SessionRefreshError extends Error {
  readonly failure: RefreshFailure;

  constructor(failure: RefreshFailure, cause: unknown) {
    super('Die Anmeldung ließ sich gerade nicht erneuern. Bitte gleich noch einmal versuchen.', {
      cause,
    });
    this.name = 'SessionRefreshError';
    this.failure = failure;
  }
}

function responseStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object' || !('response' in error)) return null;
  const response = (error as { response?: unknown }).response;
  return response instanceof Response ? response.status : null;
}

export function classifyRefreshFailure(error: unknown, online = navigator.onLine): RefreshFailure {
  const status = responseStatus(error);
  if (status === 401) return { kind: 'invalid-session', definitive: true };
  if (status === 403) return { kind: 'csrf-rejected', definitive: false };
  if (status === 409) return { kind: 'concurrent-refresh', definitive: false };
  if (status === 429) return { kind: 'rate-limited', definitive: false };
  if (status !== null && status >= 500) return { kind: 'server-error', definitive: false };
  if (error instanceof Error && error.name === 'TimeoutError') {
    return { kind: 'timeout', definitive: false };
  }
  if (!online) return { kind: 'offline', definitive: false };
  return { kind: 'network-error', definitive: false };
}
